import { BOARD_SIZE, WIN_LENGTH } from '../config/board.ts'
import { LINE_SCORE_TABLE, CLUSTER_MULTIPLIER, SCORE_MULTIPLIER } from '../config/scoring.ts'
import type { Cell, TerritoryMap, ScoreBreakdown } from './types.ts'
import { createEmptyTerritoryMap } from './types.ts'

// 4方向ベクトル（横・縦・右斜め・左斜め）
export const DIRECTIONS: readonly [number, number][] = [
  [0, 1],
  [1, 0],
  [1, 1],
  [1, -1],
] as const

// 盤面内判定
export function inBounds(row: number, col: number): boolean {
  return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE
}

// 相手の色を返す
export function opponent(color: 'black' | 'white'): 'black' | 'white' {
  return color === 'black' ? 'white' : 'black'
}

// 指定マスに石を置いた後の盤面を返す（純粋関数）
export function placeStone(
  board: Cell[][],
  row: number,
  col: number,
  color: 'black' | 'white'
): Cell[][] {
  const next = board.map(r => [...r])
  next[row][col] = color
  return next
}

// 指定方向の連長と両端空きを調べる
export function analyzeDirection(
  board: Cell[][],
  row: number,
  col: number,
  color: Cell,
  dr: number,
  dc: number
): { length: number; openEnds: number } {
  if (color === 'empty') return { length: 0, openEnds: 0 }

  let length = 1
  let openEnds = 0

  let r = row + dr
  let c = col + dc
  while (inBounds(r, c) && board[r][c] === color) {
    length++
    r += dr
    c += dc
  }
  if (inBounds(r, c) && board[r][c] === 'empty') openEnds++

  r = row - dr
  c = col - dc
  while (inBounds(r, c) && board[r][c] === color) {
    length++
    r -= dr
    c -= dc
  }
  if (inBounds(r, c) && board[r][c] === 'empty') openEnds++

  return { length, openEnds }
}

// 盤面の縄張りマップを計算（各マスが属する最大連長）
export function computeTerritory(board: Cell[][], color: 'black' | 'white'): TerritoryMap {
  const map = createEmptyTerritoryMap()

  for (const [dr, dc] of DIRECTIONS) {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c] !== color) continue
        const { length } = analyzeDirection(board, r, c, color, dr, dc)
        if (length > map[r][c]) {
          map[r][c] = length
        }
      }
    }
  }

  return map
}

// 勝利ラインを探す（5連以上が存在する場合にその座標を返す）
export function findWinLine(
  board: Cell[][],
  color: 'black' | 'white'
): [number, number][] | null {
  for (const [dr, dc] of DIRECTIONS) {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c] !== color) continue

        const pr = r - dr
        const pc = c - dc
        if (inBounds(pr, pc) && board[pr][pc] === color) continue

        const line: [number, number][] = []
        let cr = r
        let cc = c
        while (inBounds(cr, cc) && board[cr][cc] === color) {
          line.push([cr, cc])
          cr += dr
          cc += dc
        }

        if (line.length >= WIN_LENGTH) {
          return line.slice(0, WIN_LENGTH)
        }
      }
    }
  }
  return null
}

// 勝利判定
export function checkWin(board: Cell[][], color: 'black' | 'white'): boolean {
  return findWinLine(board, color) !== null
}

// 盤面満杯判定
export function isBoardFull(board: Cell[][]): boolean {
  for (const row of board) {
    for (const cell of row) {
      if (cell === 'empty') return false
    }
  }
  return true
}

// 勝利時の花演出: 勝利ライン周辺2マスに花をセット
export function buildFlowers(winLine: [number, number][]): boolean[][] {
  const flowers: boolean[][] = Array.from({ length: BOARD_SIZE }, () =>
    Array<boolean>(BOARD_SIZE).fill(false)
  )

  for (const [wr, wc] of winLine) {
    for (let dr = -2; dr <= 2; dr++) {
      for (let dc = -2; dc <= 2; dc++) {
        if (dr === 0 && dc === 0) continue
        const r = wr + dr
        const c = wc + dc
        if (inBounds(r, c)) {
          flowers[r][c] = true
        }
      }
    }
  }

  return flowers
}

// ============================
// スコア計算（純粋関数）
// ============================

// クラスタ（連結成分）検出: 8方向隣接で同色石をBFS
function findClusters(board: Cell[][], color: 'black' | 'white'): number[] {
  const visited: boolean[][] = Array.from({ length: BOARD_SIZE }, () =>
    Array<boolean>(BOARD_SIZE).fill(false)
  )
  const clusterSizes: number[] = []

  const DIRS_8: [number, number][] = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],           [0, 1],
    [1, -1],  [1, 0],  [1, 1],
  ]

  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (board[r][c] !== color || visited[r][c]) continue

      // BFSでクラスタを探索
      const queue: [number, number][] = [[r, c]]
      visited[r][c] = true
      let size = 0

      while (queue.length > 0) {
        const item = queue.shift()
        if (!item) break
        const [cr, cc] = item
        size++

        for (const [dr, dc] of DIRS_8) {
          const nr = cr + dr
          const nc = cc + dc
          if (!inBounds(nr, nc)) continue
          if (visited[nr][nc]) continue
          if (board[nr][nc] !== color) continue
          visited[nr][nc] = true
          queue.push([nr, nc])
        }
      }

      if (size >= 2) clusterSizes.push(size)
    }
  }

  return clusterSizes
}

// ライン得点を計算（最大連続列のみカウント）
// 各方向・各開始点について「先頭」の連のみを対象にすることで部分集合の重複を防ぐ
function calcLineScore(board: Cell[][], color: 'black' | 'white'): {
  lineCounts: Record<number, number>
  lineTotal: number
} {
  const lineCounts: Record<number, number> = {}

  for (const [dr, dc] of DIRECTIONS) {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c] !== color) continue

        // 逆方向の先が同色なら先頭ではない（重複スキップ）
        const pr = r - dr
        const pc = c - dc
        if (inBounds(pr, pc) && board[pr][pc] === color) continue

        // 連長を測定
        let length = 0
        let cr = r
        let cc = c
        while (inBounds(cr, cc) && board[cr][cc] === color) {
          length++
          cr += dr
          cc += dc
        }

        // 2連以上のみスコア対象
        if (length >= 2) {
          // 5連以上はまとめて5連扱い（即勝利なので実際には5のみ）
          const key = Math.min(length, 5)
          lineCounts[key] = (lineCounts[key] ?? 0) + 1
        }
      }
    }
  }

  // 合計計算
  let lineTotal = 0
  for (const [lenStr, count] of Object.entries(lineCounts)) {
    const len = Number(lenStr)
    const pts = LINE_SCORE_TABLE[len] ?? 0
    lineTotal += pts * count
  }

  return { lineCounts, lineTotal }
}

// スコア内訳を計算（盤面から純粋関数で再計算可能）
// SCORE_MULTIPLIER により色別にスコアが非対称化される（後手コミ）
export function computeScore(board: Cell[][], color: 'black' | 'white'): ScoreBreakdown {
  const { lineCounts, lineTotal } = calcLineScore(board, color)
  const clusters = findClusters(board, color)
  const clusterBonus = clusters.reduce((sum, n) => sum + n * n * CLUSTER_MULTIPLIER, 0)
  const multiplier = SCORE_MULTIPLIER[color]

  return {
    lineCounts,
    lineTotal: lineTotal * multiplier,
    clusterBonus: clusterBonus * multiplier,
    total: (lineTotal + clusterBonus) * multiplier,
  }
}

// 指定マスへの着手がスコア変化量はどのくらいか（CPU評価用）
export function scoreGainIfPlace(
  board: Cell[][],
  row: number,
  col: number,
  color: 'black' | 'white'
): number {
  const before = computeScore(board, color).total
  const nextBoard = placeStone(board, row, col, color)
  const after = computeScore(nextBoard, color).total
  return after - before
}
